<template>
  <div class="ai-assistant__sider">
    <!-- header -->
    <div class="ai-assistant__sider__header">
      <a-button type="default" block @click="addChat"><a-icon type="plus" />新增会话</a-button>
    </div>
    <!-- body -->
    <div class="ai-assistant__sider__chat-list">
      <a-space direction="vertical" :size="10" style="position: relative;width: 100%;">
        <!-- 个人话题和团队话题合并显示 -->
        <div
          v-for="(item, index) in combinedTopics"
          :key="index"
          :class="{
            'ai-assistant__sider__chat-card': true,
            'ai-assistant__sider__chat-card--acitve': isActiveSession(item),
            'ai-assistant__sider__chat-card--edit': item.mode === 'edit'
          }"
          :data-key="item.key"
          @click="onClickTopic(item)"
        >
          <div class="ai-assistant__sider__chat-title">
            <a-input
              v-if="editingSessionId === item.session_id"
              v-model="item.rename"
              size="small"
              :max-length="60"
              @pressEnter="handleSubmit(item)"
            />
            <span v-else>
              <!-- 根据话题类型显示不同的图标 -->
              <a-icon :type="item.type === 'teamSession' ? 'team' : 'user'" style="margin-right: 5px;" />
              {{ item.title }}
              <!-- 置顶标记 -->
              <a-icon v-if="item.isTopup" type="pushpin" style="margin-left: 5px; color: #1E5AF9;" />
            </span>
            <a-space style="margin-left: 10px" v-if="editingSessionId === item.session_id" class="ai-assistant__sider__chat-tools" :size="3">
            <a class="ai-assistant__sider__chat-tool" @click.stop="handleSubmit(item)">
              <a-icon type="check-circle" theme="filled" style="color: #1E5AF9" />
            </a>
            <a class="ai-assistant__sider__chat-tool" @click.stop="handleCancel(item)">
              <a-icon type="close-circle" theme="filled" style="color: #F5222D" />
            </a>
          </a-space>
          </div>
          <!-- 更多操作下拉菜单 -->
          <a-dropdown
            class="ai-assistant__sider__chat-more"
            :trigger="['click']"
            placement="bottomRight"
            v-if="editingSessionId !== item.session_id"
          >
            <a class="ai-assistant__sider__chat-more-icon">
              <a-icon type="ellipsis" />
            </a>
            <a-menu slot="overlay">
              <a-menu-item key="pin" @click="handleTogglePin(item)">
                <a-icon :type="item.isTopup ? 'pushpin' : 'pushpin'" :theme="item.isTopup ? 'filled' : 'outlined'" />
                {{ item.isTopup ? '取消置顶' : '置顶' }}
              </a-menu-item>
              <a-menu-item key="edit" @click="handleEdit(item)">
                <a-icon type="edit" />编辑
              </a-menu-item>
              <a-menu-item key="delete" @click="handleDelete(item)">
                <a-icon type="delete" />删除
              </a-menu-item>
            </a-menu>
          </a-dropdown>
          <!-- 编辑状态下的确认/取消按钮 -->
          <div class="ai-assistant__sider__chat-info">
            <span>{{ getMessageCount(item) }}条对话</span>
            <span>{{ item.updated_at }}</span>
          </div>
        </div>
      </a-space>
    </div>
    <!-- footer -->
    <div class="assistant__sider__footer">
      <a class="assistant__sider__footer__action" @click="resetSessions">
        <a-icon type="delete" />
        <div class="assistant__sider__footer__action-name">清除所有会话</div>
      </a>
    </div>

    <!-- header -->
    <div class="ai-assistant__sider__header">
      <a-button type="default" block @click="handleAgentsShow"><a-icon type="plus" />我的智能体</a-button>
    </div>
    <!-- body -->
    <div class="ai-assistant__sider__chat-list" style="flex: 0 0 200px;">
      <a-space direction="vertical" :size="10" style="width: 100%;">
        <div
          v-for="(item, index) in userAgents"
          :key="index"
          :class="{
            'ai-assistant__sider__chat-card': true,
            'ai-assistant__sider__chat-card--acitve': item.user_agent_id === user_agent_id,
          }"
          :data-key="item.key"
          @click="onClickAgent(item)"
        >
          <div class="ai-assistant__sider__chat-title">{{ item.agent_name }}</div>
          <a-space class="ai-assistant__sider__chat-tools" :size="3">
            <a-tooltip title="删除">
              <a class="ai-assistant__sider__chat-tool" @click.stop="handleUserAgentDelete(item)">
                <a-icon type="delete" />
              </a>
            </a-tooltip>
          </a-space>
        </div>
      </a-space>
    </div>

    <!-- 团队话题弹窗 -->
    <team-topic-modal
      ref="teamTopicModal"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import TeamTopicModal from './TeamTopicModal'
export default {
  components: {
    TeamTopicModal
  },
  computed: {
    ...mapGetters('aiAssistant', [
      'sessions',
      'session_id',
      'user_agent_id',
      'userAgents',
      'teamTopics'
    ]),
    ...mapGetters(['activePane']),
    // 合并个人话题和团队话题
    combinedTopics() {
      const personalTopics = (this.sessions || []).map(item => ({
        ...item,
        mode: item.mode || 'view'
      }))
      // 先按置顶状态排序，再按更新时间排序
      return [...personalTopics].sort((a, b) => {
        // 置顶的排在前面
        if (a.isTopup && !b.isTopup) return -1
        if (!a.isTopup && b.isTopup) return 1

        // 同为置顶或非置顶，按更新时间排序
        const dateA = new Date(a.updated_at)
        const dateB = new Date(b.updated_at)
        return dateB - dateA
      })
    }
  },
  watch: {
    'activePane.agentId': {
      immediate: true,
      deep: true,
      handler (val, oldVal) {
        if (oldVal && !val) {
          this.onClickSession(this.sessions[0])
          return
        }
        const item = this.userAgents.find((item) => item.agent_id === val)
        if (item) {
          this.$nextTick(() => {
            this.onClickAgent(item)
          })
        }
      }
    }
  },
  data () {
   return {
     editingSessionId: null
    }
  },
  methods: {
    addChat () {
      this.$store.dispatch('aiAssistant/addSession')
    },
    // 判断是否是当前激活的会话
    isActiveSession(item) {
      return item.session_id === this.session_id
    },
    // 获取消息数量
    getMessageCount(item) {
      return item.chats?.length || item.msg_count || 0
    },
    // 点击话题
    onClickTopic(item) {
      if (item.session_id === this.session_id) return
      this.$store.dispatch('aiAssistant/setSessionId', item.session_id)
    },
    onClickSession (item) {
      if (item.session_id === this.session_id) return
      this.$store.dispatch('aiAssistant/setSessionId', item.session_id)
    },
    onClickAgent (item) {
      if (item.user_agent_id === this.user_agent_id) return
      this.$store.dispatch('aiAssistant/setUserAgentId', item.user_agent_id)
    },
    handleEdit (item) {
      if (item.type === 'teamSession') {
        this.$refs.teamTopicModal.open(item)
      } else {
        item.rename = item.title
        item.mode = 'edit'
        this.editingSessionId = item.session_id
      }
    },
    handleSubmit (item) {
      item.mode = 'view'
      if (!item.rename?.trim?.()) {
        return
      }
      item.title = item.rename
      this.$store.dispatch('aiAssistant/send', {
        method: 'editSession',
        data: {
          type: 'session',
          session_id: item.session_id,
          title: item.title
        }
      })
      this.editingSessionId = null
    },
    handleCancel (item) {
      item.mode = 'view'
      this.editingSessionId = null
    },
    handleDelete (item) {
      if (item.type === 'teamSession') {
        this.$confirm({
          title: '确认删除',
          content: `确定要删除团队话题"${item.title}"吗？`,
          okText: '确认',
          cancelText: '取消',
          onOk: () => {
            this.$store.dispatch('aiAssistant/send', {
              method: 'clearSession',
              data: {
                session_id: item.session_id
              }
            })
          }
        })
      } else {
        this.$store.dispatch('aiAssistant/clearSession', item.session_id)
      }
    },
    clearSessions () {
      this.$store.dispatch('aiAssistant/clearSessions')
    },
    resetSessions () {
      this.$store.dispatch('aiAssistant/clearSessions')
      this.$store.dispatch('aiAssistant/addSession')
    },
    handleAgentsShow () {
      this.$store.dispatch('aiAssistant/setAgentsShow', true)
    },
    handleUserAgentDelete (item) {
      this.$store.dispatch('aiAssistant/send', {
        method: 'unbindUserAgent',
        data: {
          agent_id: item.agent_id,
          user_agent_id: item.user_agent_id
        }
      })
    },
    // 切换置顶状态
    handleTogglePin(item) {
      if (!item.isTopup) {
        this.$store.dispatch('aiAssistant/send', {
          method: 'sessionTopup',
          data: {
            session_id: item.session_id
          }
        })
      } else {
        this.$store.dispatch('aiAssistant/send', {
          method: 'sessionUnTopup',
          data: {
            session_id: item.session_id
          }
        })
      }
    }
  }
}
</script>

<style lang="less" scoped>
.ai-assistant__sider {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ai-assistant__sider__header {
  padding: 10px 10px 0;
}
.ai-assistant__sider__chat-list {
  padding: 10px;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
}
.ai-assistant__sider__chat-card {
  padding: 5px 8px;
  background-color: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  position: relative;
  cursor: pointer;
}
.ai-assistant__sider__chat-title {
  padding-right: 35px;
  color: #0B1019;
  font-size: 12px;
  font-weight: 700;
  height: 24px;
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
}
.ai-assistant__sider__chat-info {
  margin-top: 9px;
  display: flex;
  justify-content: space-between;
  color: #434B5B;
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
}
.ai-assistant__sider__chat-more {
  position: absolute;
  right: 4px;
  top: 6px;
  display: none;
}
.ai-assistant__sider__chat-card:hover .ai-assistant__sider__chat-more {
  display: inline-block;
}
.ai-assistant__sider__chat-more-icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  line-height: 16px;
  font-size: 14px;
  color: #4E535C;
  text-align: center;

  &:hover {
    color: #1E5AF9;
  }
}
.ai-assistant__sider__chat-tool {
  display: inline-block;
  width: 16px;
  height: 16px;
  line-height: 16px;
  font-size: 14px;
  color: #4E535C;
  text-align: center;

  &:hover {
    color: #1E5AF9;
  }
}
.ai-assistant__sider__chat-card:hover,
.ai-assistant__sider__chat-card--acitve {
  border-color: #1E5AF9;

  .ai-assistant__sider__chat-title {
    color: #1E5AF9;
  }
}
.assistant__sider__footer {
  padding: 5px 0 10px;
  border-bottom: 1px solid #E5E8EB;
}
.assistant__sider__footer__action {
  padding: 0 10px;
  display: flex;
  align-items: center;
  color: #0B1019;
  font-size: 12px;
  font-weight: 300;
  line-height: 28px;

  &:hover {
    color: #1E5AF9;
  }
}
.assistant__sider__footer__action-name {
  margin-left: 5px;
}
</style>
